import React from 'react';
import { useAuthStore } from '../../../store/authStore';

export type Theme = 'dark' | 'light';

interface Props {
  schoolName: string | null;
  theme: Theme;
  onToggleTheme: () => void;
  onNavigateDev?: () => void;
}

const T = {
  dark: {
    header: 'rgba(10,10,22,0.92)',
    border: 'rgba(255,255,255,0.05)',
    shadow: '0 1px 0 rgba(124,58,237,0.08)',
    logo: 'text-white',
    sub: '#6c7086',
    schoolBg: 'rgba(124,58,237,0.08)',
    schoolBorder: 'rgba(124,58,237,0.2)',
    schoolText: '#cba6f7',
    devBg: 'rgba(255,255,255,0.04)',
    devBorder: 'rgba(255,255,255,0.07)',
    devText: '#6c7086',
    logoutBg: 'rgba(243,139,168,0.06)',
    logoutBorder: 'rgba(243,139,168,0.15)',
    logoutText: '#f38ba8',
    themeBg: 'rgba(255,255,255,0.04)',
    themeBorder: 'rgba(255,255,255,0.07)',
    themeText: '#a6adc8',
    liveBg: 'rgba(166,227,161,0.08)',
    liveBorder: 'rgba(166,227,161,0.15)',
    liveText: '#a6e3a1',
    divider: 'rgba(255,255,255,0.06)',
  },
  light: {
    header: 'rgba(255,255,255,0.95)',
    border: 'rgba(0,0,0,0.06)',
    shadow: '0 1px 0 rgba(124,58,237,0.1)',
    logo: 'text-slate-900',
    sub: '#94a3b8',
    schoolBg: 'rgba(124,58,237,0.06)',
    schoolBorder: 'rgba(124,58,237,0.2)',
    schoolText: '#7c3aed',
    devBg: 'rgba(0,0,0,0.03)',
    devBorder: 'rgba(0,0,0,0.08)',
    devText: '#64748b',
    logoutBg: 'rgba(239,68,68,0.05)',
    logoutBorder: 'rgba(239,68,68,0.15)',
    logoutText: '#ef4444',
    themeBg: 'rgba(0,0,0,0.03)',
    themeBorder: 'rgba(0,0,0,0.08)',
    themeText: '#64748b',
    liveBg: 'rgba(34,197,94,0.08)',
    liveBorder: 'rgba(34,197,94,0.2)',
    liveText: '#16a34a',
    divider: 'rgba(0,0,0,0.06)',
  },
};

export function StudioHeader({ schoolName, theme, onToggleTheme, onNavigateDev }: Props) {
  const { logout } = useAuthStore();
  const s = T[theme];

  return (
    <header
      className="flex items-center justify-between px-6 flex-shrink-0"
      style={{
        height: 52,
        background: s.header,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${s.border}`,
        boxShadow: s.shadow,
      }}
    >
      {/* Logo + branding */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              boxShadow: '0 2px 10px rgba(124,58,237,0.35)',
            }}
          >
            <span className="text-sm">🎓</span>
          </div>
          <div>
            <div className={`font-black text-sm tracking-tight leading-none ${s.logo}`}>
              Aluno Conectado
            </div>
            <div className="text-[9px] leading-none mt-0.5 font-semibold tracking-widest uppercase" style={{ color: s.sub }}>
              Simulation Studio
            </div>
          </div>
        </div>

        <div className="h-5 w-px" style={{ background: s.divider }} />

        {/* Badge ao vivo */}
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{ background: s.liveBg, border: `1px solid ${s.liveBorder}` }}
        >
          <span className="relative flex h-1.5 w-1.5">
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              style={{ background: s.liveText }}
            />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: s.liveText }} />
          </span>
          <span className="text-[10px] font-semibold" style={{ color: s.liveText }}>Ao Vivo</span>
        </div>
      </div>

      {/* Ações lado direito */}
      <div className="flex items-center gap-2">
        {schoolName && (
          <div
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: s.schoolBg, border: `1px solid ${s.schoolBorder}` }}
          >
            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: s.schoolText }} />
            <span className="text-[11px] font-medium truncate max-w-[180px]" style={{ color: s.schoolText }}>
              {schoolName}
            </span>
          </div>
        )}

        {/* Toggle tema */}
        <button
          onClick={onToggleTheme}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all cursor-pointer"
          style={{ background: s.themeBg, border: `1px solid ${s.themeBorder}`, color: s.themeText }}
          title={theme === 'dark' ? 'Mudar para modo claro' : 'Mudar para modo escuro'}
        >
          {theme === 'dark' ? '☀️ Claro' : '🌙 Escuro'}
        </button>

        {onNavigateDev && (
          <button
            onClick={onNavigateDev}
            className="text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all cursor-pointer"
            style={{ background: s.devBg, border: `1px solid ${s.devBorder}`, color: s.devText }}
          >
            ⚙ /dev
          </button>
        )}

        <button
          onClick={logout}
          className="text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all cursor-pointer"
          style={{ background: s.logoutBg, border: `1px solid ${s.logoutBorder}`, color: s.logoutText }}
        >
          Sair
        </button>
      </div>
    </header>
  );
}
